// Color helpers for the launcher button.

/**
 * Resolve any CSS color string (hex, rgb(), hsl(), named) to an `[r, g, b]`
 * triplet by letting the browser parse it via a throwaway element. Returns
 * null when the environment can't resolve it (e.g. jsdom without layout, or
 * an invalid color).
 */
export function resolveRgb(color: string, doc: Document = document): [number, number, number] | null {
  try {
    const probe = doc.createElement('span');
    probe.style.color = '';
    probe.style.color = color;
    // An invalid color leaves style.color === '' — bail early.
    if (probe.style.color === '') return null;
    probe.style.display = 'none';
    doc.body.appendChild(probe);
    const resolved = (doc.defaultView ?? window).getComputedStyle(probe).color;
    probe.remove();
    // Real browsers always compute `color` to `rgb()/rgba()`. Bail on anything
    // else (e.g. a jsdom build that echoes back hex) so we never mis-parse the
    // digits of a hex string.
    if (!/^rgba?\(/i.test(resolved)) return null;
    const nums = resolved.match(/[\d.]+/g);
    if (nums === null || nums.length < 3) return null;
    return [Number(nums[0]), Number(nums[1]), Number(nums[2])];
  } catch {
    return null;
  }
}

/**
 * Pick a readable text/icon color (near-black or white) for content placed on
 * `bg`, using perceived luminance. Returns null when `bg` can't be resolved
 * so callers can fall back to a theme default.
 */
export function readableTextColor(bg: string, doc: Document = document): string | null {
  const rgb = resolveRgb(bg, doc);
  if (rgb === null) return null;
  const [r, g, b] = rgb;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#0f172a' : '#ffffff';
}
